import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, BookOpen, ArrowRight } from "lucide-react";
import { TutorialViewerModal } from "../pages/WorkerTutorialsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginTutorialsModal = ({ onClose }) => {
  const [mode, setMode] = useState("menu"); // menu | global | company
  const [globals, setGlobals] = useState([]);
  const [code, setCode] = useState("");
  const [companyTutorials, setCompanyTutorials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openItem, setOpenItem] = useState(null);

  useEffect(() => {
    axios.get(`${API}/global-tutorials`).then(r => setGlobals(r.data || [])).catch(() => {});
  }, []);

  const loadCompany = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setLoading(true); setError("");
    try {
      const res = await axios.get(`${API}/tutorials/by-code/${code.trim().toUpperCase()}`);
      setCompanyTutorials(res.data || []);
      setMode("company");
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't load tutorials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="login-tutorials-modal">
      <div className="bg-white rounded-md max-w-2xl w-full max-h-[95vh] flex flex-col shadow-2xl">
        <div className="border-b-2 p-4 flex items-center justify-between sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-orange-500" />
            <h2 className="text-xl font-bold uppercase">Tutorials</h2>
          </div>
          <button onClick={onClose} data-testid="close-login-tutorials"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {mode === "menu" && (
            <div className="space-y-4">
              <button
                onClick={() => setMode("global")}
                className="w-full text-left bg-white border-2 border-slate-200 hover:border-orange-400 rounded-md p-4 flex items-center justify-between"
                data-testid="open-global-tutorials"
              >
                <div>
                  <div className="font-bold text-slate-900">Get Started Basics</div>
                  <div className="text-xs text-slate-500">How to log in, basic navigation, what each tab does ({globals.length} guide{globals.length === 1 ? "" : "s"})</div>
                </div>
                <ArrowRight className="w-5 h-5 text-orange-500" />
              </button>

              <div className="bg-white border-2 border-slate-200 rounded-md p-4">
                <div className="font-bold text-slate-900 mb-1">Your Company's Tutorials</div>
                <div className="text-xs text-slate-500 mb-3">Got your Company ID? Enter it below to see tutorials specific to your team.</div>
                <form onSubmit={loadCompany} className="flex gap-2">
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="e.g. MIRRI"
                    className="flex-1 border-2 border-slate-200 rounded-md px-3 py-2 font-mono uppercase"
                    data-testid="tutorials-code-input"
                  />
                  <button type="submit" disabled={loading || !code.trim()} className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded font-bold" data-testid="tutorials-load-by-code">
                    {loading ? "Loading…" : "View"}
                  </button>
                </form>
                {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
              </div>
            </div>
          )}

          {mode === "global" && (
            <div>
              <button onClick={() => setMode("menu")} className="text-xs text-orange-600 hover:underline mb-3">← Back</button>
              {globals.length === 0 ? (
                <div className="text-center py-10 text-slate-400">No global guides published yet.</div>
              ) : (
                <div className="space-y-2">
                  {globals.map(t => (
                    <button key={t.id} onClick={() => setOpenItem(t)} className="w-full text-left bg-white border-2 border-slate-200 hover:border-orange-400 rounded p-3" data-testid={`global-tutorial-${t.id}`}>
                      <div className="font-semibold">{t.title}</div>
                      <div className="text-xs text-slate-500 line-clamp-2">{t.body_markdown?.slice(0, 120)}…</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {mode === "company" && (
            <div>
              <button onClick={() => setMode("menu")} className="text-xs text-orange-600 hover:underline mb-3">← Back</button>
              <div className="text-xs text-slate-500 mb-2">Showing tutorials for company <strong>{code.toUpperCase()}</strong></div>
              {companyTutorials.length === 0 ? (
                <div className="text-center py-10 text-slate-400">No tutorials published yet for this company.</div>
              ) : (
                <div className="space-y-2">
                  {companyTutorials.map(t => (
                    <button key={t.id} onClick={() => setOpenItem(t)} className="w-full text-left bg-white border-2 border-slate-200 hover:border-orange-400 rounded p-3" data-testid={`company-tutorial-${t.id}`}>
                      <div className="font-semibold">{t.title}</div>
                      <div className="text-xs text-slate-500 line-clamp-2">{t.summary || t.body_markdown?.slice(0, 120)}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {openItem && <TutorialViewerModal tutorial={openItem} onClose={() => setOpenItem(null)} />}
    </div>
  );
};

export default LoginTutorialsModal;
